import axiosInstance from "../api/axiosInstance";
import { API_URL, ENDPOINTS } from "../constants/api";

export interface Comment {
  id: number;
  comment: string;
  datetime: number;
  sessionid: number;
  user: {
    id: number;
    name: string;
    profilepicid?: string;
  };
}

export interface SessionUser {
  id: number;
  name: string;
  profilepicid?: string;
  ispro?: boolean;
  freesubscription?: boolean;
}

export interface SessionSpot {
  id: number;
  name: string;
  latitude?: number;
  longitude?: number;
}

export interface SessionDevice {
  brand?: string;
  model?: string;
}

export interface SessionJump {
  id: number;
  nr: number;
  timestring?: string;
  airtime?: number;
  height?: number;
  distance?: number;
  maxspeedreached?: number;
  gpsstartlat?: number;
  gpsstartlon?: number;
  gpsendlat?: number;
  gpsendlon?: number;
  gpsstartepoch?: number;
  gpsendepoch?: number;
  gpsstartwarning?: boolean;
  gpsendwarning?: boolean;
  speed?: number;
  bearing?: number;
  angle?: number;
  rotations?: number;
}

export interface SessionGpsPoint {
  latitude: number;
  longitude: number;
  altitude?: number;
  timestamp?: number;
  speed?: number;
  distance?: number;
  accuracy?: number;
  epoch?: number;
  nr?: number;
}

export interface Session {
  ID: number;
  startepoch: number;
  remark?: string;
  totalairtime?: number;
  totaldistance?: number;
  totalheight?: number;
  timeonwater?: number;
  maxheight?: number;
  maxdistance?: number;
  maxairtime?: number;
  maxspeed?: number;
  maxpopgforce?: number;
  maxlandinggforce?: number;
  totaldistancekitesurfing?: number;
  airtimepr?: boolean;
  heightpr?: boolean;
  speedpr?: boolean;
  distancepr?: boolean;
  commentscount?: number;
  likescount?: number;
  likedByCaller?: boolean;
  winddir?: number;
  winddirvalidated?: boolean;
  sessionpicid?: string;
  boardtype?: string;
  kite?: string;
  userkiteid?: number;
  visiblesession?: boolean;
  freetrialsession?: boolean;
  surfrai?: boolean;
  createDate?: string;
  user?: SessionUser;
  spot?: SessionSpot;
  device?: SessionDevice;
  jumps?: SessionJump[];
  gps?: SessionGpsPoint[];
  // GPS bounding box
  gpsstartlat?: number;
  gpsstartlon?: number;
  gpsendlat?: number;
  gpsendlon?: number;
  gpsavglat?: number;
  gpsavglon?: number;
  gpsminlat?: number;
  gpsminlon?: number;
  gpsmaxlat?: number;
  gpsmaxlon?: number;
  // Additional fields
  mode?: number;
  jumpcount?: number;
  garminsessionid?: string;
}

/** One detected clock discontinuity inside a GPS track (SRF-2710). */
export interface SessionTimeJump {
  index: number;
  epochBefore: number;
  epochAfter: number;
  offsetMs: number;
}

/**
 * Result of analysing a session's GPS track for a mid-session device-clock jump.
 * `repairable` is false either because nothing is wrong or because the track needs
 * manual review — `reason` says which.
 */
export interface SessionTimeJumpAnalysis {
  hasJumps: boolean;
  repairable: boolean;
  reason: string | null;
  pointCount: number;
  originalStartEpoch: number;
  originalEndEpoch: number;
  originalDurationSeconds: number;
  repairedStartEpoch: number;
  repairedEndEpoch: number;
  repairedDurationSeconds: number;
  jumps: SessionTimeJump[];
}

export interface RepairTimeJumpResult {
  message: string;
  startepoch: number;
  timeonwater: number;
  jumpsShifted: number;
  analysis: SessionTimeJumpAnalysis;
}

/** Outcome recorded by the Garmin import function next to the FIT file (outcome.json). Absent for files imported before it existed. */
export type GarminImportOutcome =
  | "SAVED"
  | "IGNORED_NOT_SURFR_APP"
  | "IGNORED_TOO_SHORT"
  | "IGNORED_NO_DATA"
  | "FAILED";

export interface GarminFile {
  activityId: string;
  fileSize: number | null;
  fileDate: number | null;
  sessionId: number | null;
  sessionDate: number | null;
  outcome?: GarminImportOutcome | null;
  outcomeReason?: string | null;
  appName?: string | null;
  sportType?: string | null;
  outcomeAt?: number | null;
}

export const SessionsService = {
  /**
   * Get access token from localStorage
   */
  getAccessToken(): string {
    const userJson = localStorage.getItem("user-object");
    const user = userJson ? JSON.parse(userJson) : null;
    return user?.accesstoken || "";
  },

  /**
   * List sessions for a user with pagination
   */
  async listUserSessions(
    userId: number,
    page: number = 0,
    options?: {
      spotId?: number;
      boardtype?: string;
      photoonly?: boolean;
    }
  ): Promise<Session[]> {
    const accessToken = this.getAccessToken();
    const params = new URLSearchParams();
    params.append("userid", String(userId));
    params.append("accesstoken", accessToken);
    params.append("brodown", "true");

    if (options?.spotId) params.append("spotid", String(options.spotId));
    if (options?.boardtype) params.append("boardtype", options.boardtype);
    if (options?.photoonly) params.append("photoonly", "true");

    const response = await axiosInstance.get(`/sessions/list/${page}?${params.toString()}`);
    return response.data;
  },

  /**
   * Get session details with jumps and GPS data
   */
  async getSessionDetails(
    sessionId: number,
    options?: {
      includedetails?: boolean;
      loadJumps?: boolean;
      loadGps?: boolean;
    }
  ): Promise<Session> {
    const accessToken = this.getAccessToken();
    const params = new URLSearchParams();

    if (options?.includedetails) params.append("includedetails", "true");
    if (options?.loadJumps !== false) params.append("loadJumps", "true");
    if (options?.loadGps !== false) params.append("loadGps", "true");

    const queryString = params.toString();
    const url = `/sessions/${accessToken}/${sessionId}${queryString ? `?${queryString}` : ""}`;

    const response = await axiosInstance.get(url);
    return response.data;
  },

  /**
   * Get session jumps
   */
  async getSessionJumps(sessionId: number, includeDetails: boolean = false): Promise<SessionJump[]> {
    const accessToken = this.getAccessToken();
    const params = includeDetails ? "?includedetails=true" : "";

    const response = await axiosInstance.get(`/sessions/${accessToken}/${sessionId}/jumps${params}`);
    return response.data;
  },

  /**
   * Get session GPS track
   */
  async getSessionGps(sessionId: number): Promise<SessionGpsPoint[]> {
    const accessToken = this.getAccessToken();

    const response = await axiosInstance.get(`/sessions/${accessToken}/${sessionId}/gps`);
    return response.data;
  },

  /**
   * Get session image URL
   */
  getSessionImageUrl(sessionPicId: string): string {
    return `${API_URL}/image/session/${sessionPicId}`;
  },

  /**
   * Get user profile image URL
   */
  getProfileImageUrl(profilePicId: string): string {
    return `${API_URL}/image/profile/${profilePicId}`;
  },

  /**
   * Format duration in seconds to human readable string
   */
  formatDuration(seconds?: number): string {
    if (!seconds) return "-";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  },

  /**
   * Format date from epoch
   */
  formatDate(epoch?: number): string {
    if (!epoch) return "-";
    const date = new Date(epoch);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  },

  /**
   * Get wind direction as compass string
   */
  getWindDirection(degrees?: number): string {
    if (!degrees || degrees === 0) return "-";
    const directions = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  },

  /**
   * Get GDPR export download URL for a user (direct browser download)
   */
  getGdprExportUrl(userId: number): string {
    const baseUrl = axiosInstance.defaults.baseURL || "";
    const accessToken = this.getAccessToken();
    return `${baseUrl}${ENDPOINTS.ADMIN.GDPR_EXPORT(userId)}?accesstoken=${accessToken}`;
  },

  /**
   * Update session datetime (admin)
   */
  async updateSessionDatetime(sessionId: number, datetime: string, reason?: string): Promise<{ startepoch: number }> {
    const response = await axiosInstance.put(ENDPOINTS.ADMIN.UPDATE_SESSION_DATETIME(sessionId), { datetime, reason });
    return response.data;
  },

  /**
   * Detect a mid-session GPS/device-clock jump (SRF-2710). Read-only preview.
   */
  async detectSessionTimeJump(sessionId: number): Promise<SessionTimeJumpAnalysis> {
    const response = await axiosInstance.get(ENDPOINTS.ADMIN.DETECT_SESSION_TIME_JUMP(sessionId));
    return response.data;
  },

  /**
   * Repair a mid-session GPS/device-clock jump (SRF-2710): shifts the stale prefix
   * onto the GPS-locked tail and recomputes the session date, duration and jumps.
   */
  async repairSessionTimeJump(sessionId: number, reason?: string): Promise<RepairTimeJumpResult> {
    const response = await axiosInstance.post(ENDPOINTS.ADMIN.REPAIR_SESSION_TIME_JUMP(sessionId), { reason });
    return response.data;
  },

  /**
   * Download the Garmin FIT file for a session
   */
  async downloadFitFile(sessionId: number): Promise<void> {
    const response = await axiosInstance.get(ENDPOINTS.ADMIN.FIT_DOWNLOAD(sessionId));
    const { url } = response.data;
    window.open(url, "_blank");
  },

  /**
   * List all Garmin FIT files for a user from GCS
   */
  async listGarminFiles(userId: number): Promise<GarminFile[]> {
    const response = await axiosInstance.get(ENDPOINTS.ADMIN.GARMIN_FILES(userId));
    return response.data;
  },

  /**
   * Download a Garmin FIT file by userId and activityId
   */
  async downloadGarminFile(userId: number, activityId: string): Promise<void> {
    const response = await axiosInstance.get(ENDPOINTS.ADMIN.GARMIN_FILE_DOWNLOAD(userId, activityId));
    const { url } = response.data;
    window.open(url, "_blank");
  },

  /**
   * List comments for a session
   */
  async listSessionComments(sessionId: number): Promise<Comment[]> {
    const response = await axiosInstance.get(`/comments/list/0?sessionid=${sessionId}`);
    return response.data;
  },

  /**
   * Update a comment's text (admin)
   */
  async updateComment(commentId: number, text: string): Promise<void> {
    await axiosInstance.put(ENDPOINTS.ADMIN.UPDATE_COMMENT(commentId), { comment: text });
  },

  /**
   * Delete a comment (admin)
   */
  async deleteComment(commentId: number): Promise<void> {
    await axiosInstance.delete(ENDPOINTS.ADMIN.DELETE_COMMENT(commentId));
  },

  /**
   * Get board type display name
   */
  getBoardTypeLabel(boardtype?: string): string {
    const types: Record<string, string> = {
      twintip: "Twin Tip",
      directional: "Directional",
      foil: "Foil",
      landboarding: "Landboarding",
      snowkiting: "Snowkiting",
      windsurfing: "Windsurfing",
      wingfoiling: "Wing Foiling",
    };
    return boardtype ? types[boardtype] || boardtype : "-";
  },
};
